import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { quote: "", author: "", role: "", order: 0 };

export default function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/testimonials").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onChange = (k) => (e) => {
    const v = e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const startEdit = (t) => { setEditingId(t.id); setForm({ quote: t.quote, author: t.author, role: t.role || "", order: t.order }); };
  const cancel = () => { setEditingId(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.quote || !form.author) { toast.error("Quote and author are required."); return; }
    setSaving(true);
    try {
      if (editingId) await api.put(`/testimonials/${editingId}`, form);
      else await api.post("/testimonials", form);
      toast.success(editingId ? "Testimonial updated." : "Testimonial added.");
      cancel(); await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save.");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/testimonials/${id}`); toast.success("Deleted."); if (editingId === id) cancel(); await load(); }
    catch { toast.error("Delete failed."); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="testimonials-admin">
      <section className="lg:col-span-7 space-y-3">
        <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-2">Testimonials ({items.length})</p>
        {items.map((t) => (
          <div key={t.id} className={`bg-white border p-4 ${editingId === t.id ? "border-[#FF3E00]" : "border-neutral-200"}`} data-testid={`testimonial-row-${t.id}`}>
            <blockquote className="font-display font-light text-base leading-snug">&ldquo;{t.quote}&rdquo;</blockquote>
            <div className="flex justify-between items-end mt-3">
              <div className="font-body text-xs">
                <div className="font-semibold">{t.author}</div>
                {t.role && <div className="text-neutral-500">{t.role}</div>}
                <div className="text-neutral-400 mt-1">order {t.order}</div>
              </div>
              <div className="flex gap-4">
                <button onClick={() => startEdit(t)} className="font-body text-xs uppercase tracking-[0.2em] link-line" data-testid={`tst-edit-${t.id}`}>Edit</button>
                <button onClick={() => remove(t.id)} className="font-body text-xs uppercase tracking-[0.2em] text-[#FF3E00] link-line" data-testid={`tst-delete-${t.id}`}>Delete</button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="font-body text-sm text-neutral-500">No testimonials yet.</p>}
      </section>

      <aside className="lg:col-span-5">
        <div className="flex items-center justify-between mb-6">
          <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">{editingId ? "Edit Testimonial" : "New Testimonial"}</p>
          {editingId && <button onClick={cancel} className="font-body text-xs uppercase tracking-[0.2em] link-line">Cancel</button>}
        </div>
        <form onSubmit={submit} className="space-y-1 bg-white border border-neutral-200 p-6">
          <textarea value={form.quote} onChange={onChange("quote")} placeholder="Quote" rows={5} className="input-bare resize-none" data-testid="tst-quote-input" />
          <input value={form.author} onChange={onChange("author")} placeholder="Author name" className="input-bare" data-testid="tst-author-input" />
          <input value={form.role} onChange={onChange("role")} placeholder="Role (e.g. Founder, Folio Press)" className="input-bare" data-testid="tst-role-input" />
          <input type="number" value={form.order} onChange={onChange("order")} placeholder="Order" className="input-bare" data-testid="tst-order-input" />
          <button type="submit" disabled={saving} className="mt-6 w-full bg-[#0A0A0A] text-[#F4F4F0] py-4 font-display font-bold uppercase tracking-widest text-sm hover:bg-[#FF3E00] transition-colors disabled:opacity-40" data-testid="tst-save-button">
            {saving ? "Saving…" : editingId ? "Update Testimonial" : "Add Testimonial"}
          </button>
        </form>
      </aside>
    </div>
  );
}
